import { Dispatch } from "redux";

import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { UaaExtendedUser, UaaOidcUser, UaaUser } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { WorkflowsActions } from "@com.mgmtp.a12.workflows/workflows-core/lib/actions";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { FachverfahrenManifest } from "../../../app/slices/fachverfahren/types";
import { ProcessDefinitionKey } from "../../../utilities/processDefinitionKey";
import { isJsonRpc2ResponseArray, modulfStartProcess } from "../../../utilities/rpcHelper";
import { VALIDATION_ACTIONS } from "../../../utilities/workflowUtils";
import { DocumentWithFachverfahrenData } from "../../workflows/data/fachverfahrenData";
import { RESOURCE_KEYS } from "../../../localization";
import { createCustomNotificationAction } from "../../../app/actions";

const logger = LoggerFactory.getLogger("modulf");

export type ModulfUser = UaaUser | UaaOidcUser | UaaExtendedUser;

export async function createNewAntrag(
	dispatch: Dispatch,
	client: string,
	fachverfahrenManifest: FachverfahrenManifest,
	activityId: string,
	initialAntragsdata?: DocumentWithFachverfahrenData
): Promise<void> {
	dispatch(ApplicationActions.setBusy(true));

	const processDefinition = ProcessDefinitionKey.fromString(fachverfahrenManifest.processDefinitionId);
	const processDefinitionKey = ProcessDefinitionKey.toString(processDefinition);

	const document = {
		root: {
			antrag: initialAntragsdata ? initialAntragsdata.root.antrag : {},
			processMetadata: {
				fachverfahren: {
					id: processDefinition.fachverfahrenId,
					key: fachverfahrenManifest.identification.key,
					client: client || undefined,
					erstelltInVersion: fachverfahrenManifest.identification.version,
					shortName: fachverfahrenManifest.identification.shortName
				}
			},
			genehmigungsverfahren: {
				...initialAntragsdata?.root.genehmigungsverfahren,
				prozessschritte: {
					validationTyp: VALIDATION_ACTIONS.NULL
				}
			}
		}
	};

	if (initialAntragsdata) {
		document.root.antrag = initialAntragsdata.root.antrag;
	}

	modulfStartProcess(processDefinition.fachverfahrenId + "_Prozess_WF", document, processDefinitionKey)
		.then(documentReference => {
			dispatch(
				WorkflowsActions.startProcess.done({
					params: {
						processDefinition: processDefinitionKey,
						businessKey: documentReference,
						variables: {
							initialActivityId: activityId,
							documentReference
						}
					},
					result: {}
				})
			);
		})
		.catch(error => {
			handleStartProcessFailed(dispatch, processDefinitionKey, activityId, error);
		});
}

function handleStartProcessFailed(
	dispatch: Dispatch,
	processDefinitionKey: string,
	activityId: string,
	error?: unknown
): void {
	logger.error(error);
	if (isJsonRpc2ResponseArray(error) && hasErrorCode(error, 422)) {
		dispatch(
			createCustomNotificationAction({
				activityId,
				severity: "error",
				message: { key: RESOURCE_KEYS.wf.notification.error.outdatedProcessVersion.message },
				title: { key: RESOURCE_KEYS.wf.notification.error.outdatedProcessVersion.title }
			})
		);
	} else {
		dispatch(
			WorkflowsActions.startProcess.failed({
				params: {
					processDefinition: processDefinitionKey,
					businessKey: "",
					variables: {
						initialActivityId: activityId
					}
				},
				error: new Error(`Failed to start process ${processDefinitionKey}.`)
			})
		);
	}

	dispatch(ApplicationActions.setBusy(false));
}

function hasErrorCode(rpcResponses: JsonRpc2Response[], errorCode: number): boolean {
	return rpcResponses.some(
		response => JsonRpc2Response.JsonRpc2Error.isInstance(response.error) && response.error.code === errorCode
	);
}
